import { useState } from 'react';
import { Download, FileBarChart, FileSpreadsheet, FileText, Info, Receipt } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DestructiveActionChip } from '@/ui/DestructiveActionChip';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { Notificacao } from '@/ui/Notificacao';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { StatusBadge } from '@/ui/StatusBadge';
import { SearchableSelect } from '@/ui/base/searchable-select';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { Alert, AlertDescription } from '@/ui/base/alert';

import { useDres } from '../hooks/useDres';
import {
  useRegistrarExportacaoFiscal,
  useRelatorioFiscalConsolidado,
  useRelatorioFiscalCredenciado,
} from '../hooks/useSofExtras';
import type { RelatorioFiscalConsolidadoDre, RelatorioFiscalCredenciado } from '../types';
import {
  exportarRelatorioFiscalCsv,
  exportarRelatorioFiscalPdf,
  exportarRelatorioFiscalXlsx,
} from './exportarRelatorioFiscal';
import { competenciaAtual, MESES } from './mesesRelatorio';
import { SofHeader } from './SofHeader';
import { SofTabsNav } from './SofTabsNav';

const formatarMoeda = (valor: string) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));

/** FIN-031.5: relatórios fiscais extraídos do SOF — consolidado por DRE e por
 * credenciado. PDF exige confirmação digitada (registro definitivo, com
 * protocolo SEI); CSV/XLSX são pré-visualização de auditoria e saem na hora. */
export function SofRelatoriosPage() {
  const [mes, definirMes] = useState(competenciaAtual());
  const [dreId, definirDreId] = useState<string | null>(null);
  const { data: dres } = useDres();
  const consolidado = useRelatorioFiscalConsolidado(mes || undefined, dreId ?? undefined);
  const credenciados = useRelatorioFiscalCredenciado(mes || undefined, dreId ?? undefined);
  const registrar = useRegistrarExportacaoFiscal();

  const [pdfPendente, definirPdfPendente] = useState<'consolidado' | 'credenciados' | null>(null);
  const [textoConfirmacao, definirTextoConfirmacao] = useState('');
  const [aviso, definirAviso] = useState<string | null>(null);

  const totalConsolidado = (consolidado.data ?? []).reduce(
    (s, r) => s + parseFloat(r.valorLiquido),
    0,
  );
  const totalCredenciados = (credenciados.data ?? []).reduce(
    (s, r) => s + parseFloat(r.valorLiquido),
    0,
  );

  const dreAtual = dres?.find((d) => d.id === dreId);
  const competencia = mes || competenciaAtual();

  function payloadConsolidado() {
    return {
      titulo: 'Consolidado por DRE',
      competencia,
      cabecalhos: ['DRE', 'Credenciados', 'Bruto', 'Líquido', 'Distribuído', 'Pago', 'Status'],
      linhas: (consolidado.data ?? []).map((l) => [
        l.dreNome,
        String(l.totalCredenciados),
        l.valorBruto,
        l.valorLiquido,
        l.valorDistribuido,
        l.valorPago,
        l.status,
      ]),
      nomeBase: `consolidado-dre-${competencia}`,
    };
  }

  function payloadCredenciados() {
    return {
      titulo: 'Detalhamento por Credenciado',
      competencia,
      cabecalhos: ['DRE', 'Credenciado', 'Demonstrativo', 'Líquido', 'Status'],
      linhas: (credenciados.data ?? []).map((l) => [
        l.dreNome,
        l.credenciadoNome,
        l.demonstrativo,
        l.valorLiquido,
        l.status,
      ]),
      nomeBase: `credenciados-${competencia}`,
    };
  }

  async function exportarCsvOuXlsx(tipo: 'consolidado' | 'credenciados', formato: 'CSV' | 'XLSX') {
    const payload = tipo === 'consolidado' ? payloadConsolidado() : payloadCredenciados();
    if (payload.linhas.length === 0) return;
    if (formato === 'CSV') exportarRelatorioFiscalCsv(payload);
    else exportarRelatorioFiscalXlsx(payload);
    const valor = tipo === 'consolidado' ? totalConsolidado : totalCredenciados;
    await registrar.mutateAsync({
      tipo: payload.titulo,
      formato,
      competencia,
      registros: payload.linhas.length,
      valor,
    });
    definirAviso(`${payload.titulo} exportado em ${formato}.`);
  }

  async function confirmarPdf() {
    if (!pdfPendente) return;
    const payload = pdfPendente === 'consolidado' ? payloadConsolidado() : payloadCredenciados();
    exportarRelatorioFiscalPdf(payload);
    const valor = pdfPendente === 'consolidado' ? totalConsolidado : totalCredenciados;
    await registrar.mutateAsync({
      tipo: payload.titulo,
      formato: 'PDF',
      competencia,
      registros: payload.linhas.length,
      valor,
    });
    definirAviso(`${payload.titulo} exportado em PDF com protocolo SEI.`);
    definirPdfPendente(null);
    definirTextoConfirmacao('');
  }

  const colunasConsolidado: DataTableColumn<RelatorioFiscalConsolidadoDre>[] = [
    { header: 'DRE', accessorKey: 'dreNome' },
    {
      header: 'Cred.',
      accessorKey: 'totalCredenciados',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Bruto',
      cell: (l) => <MoneyDisplay value={l.valorBruto} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Líquido',
      cell: (l) => <MoneyDisplay value={l.valorLiquido} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Distribuído',
      cell: (l) => <MoneyDisplay value={l.valorDistribuido} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    {
      header: 'Pago',
      cell: (l) => <MoneyDisplay value={l.valorPago} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    { header: 'Status', cell: (l) => <StatusBadge status="aprovado" label={l.status} /> },
  ];

  const colunasCredenciado: DataTableColumn<RelatorioFiscalCredenciado>[] = [
    { header: 'DRE', accessorKey: 'dreNome' },
    { header: 'Credenciado', accessorKey: 'credenciadoNome' },
    { header: 'Demonstrativo', accessorKey: 'demonstrativo', cellClassName: 'font-mono text-xs' },
    {
      header: 'Líquido',
      cell: (l) => <MoneyDisplay value={l.valorLiquido} />,
      cellClassName: 'text-right',
      headerClassName: 'text-right',
    },
    { header: 'Status', cell: (l) => <StatusBadge status="pago" label={l.status} /> },
  ];

  function BotoesExportacao({
    tipo,
    semDados,
  }: {
    tipo: 'consolidado' | 'credenciados';
    semDados: boolean;
  }) {
    return (
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={semDados}
          onClick={() => void exportarCsvOuXlsx(tipo, 'CSV')}
        >
          <FileText className="mr-1 h-4 w-4" aria-hidden="true" /> CSV
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={semDados}
          onClick={() => void exportarCsvOuXlsx(tipo, 'XLSX')}
        >
          <FileSpreadsheet className="mr-1 h-4 w-4" aria-hidden="true" /> XLSX
        </Button>
        <Button
          size="sm"
          disabled={semDados}
          onClick={() => {
            definirPdfPendente(tipo);
            definirTextoConfirmacao('');
          }}
        >
          <Download className="mr-1 h-4 w-4" aria-hidden="true" /> PDF
        </Button>
      </div>
    );
  }

  const campos: FilterFieldConfig[] = [
    {
      key: 'mes',
      label: 'Competência',
      type: 'select',
      options: MESES,
      showAllOption: false,
      width: 'w-full sm:w-[200px]',
    },
  ];

  const pendente = pdfPendente
    ? pdfPendente === 'consolidado'
      ? payloadConsolidado()
      : payloadCredenciados()
    : null;

  return (
    <div className="space-y-6">
      <SofHeader />

      <SofTabsNav />

      {aviso && <Notificacao tom="sucesso" mensagem={aviso} aoFechar={() => definirAviso(null)} />}

      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="flex flex-wrap items-center gap-2 text-sm">
          <DestructiveActionChip label="Registro definitivo" tone="warning" />
          <span>
            Exportações em <strong>PDF</strong> geram protocolo SEI com hash SHA-256 e ficam
            imutáveis no histórico. CSV e XLSX são apenas pré-visualização de auditoria.
          </span>
        </AlertDescription>
      </Alert>

      <FilterBar
        fields={campos}
        values={{ mes }}
        onChange={(_, valor) => definirMes(valor)}
        onClear={() => {
          definirMes(competenciaAtual());
          definirDreId(null);
        }}
        hasActiveFilters={mes !== competenciaAtual() || dreId !== null}
      >
        <div className="space-y-1 sm:w-[280px]">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <SearchableSelect
            value={dreId}
            onChange={definirDreId}
            options={(dres ?? []).map((d) => ({ value: d.id, label: d.nome ?? d.codigo ?? d.id }))}
            placeholder="Todas as DREs"
            searchPlaceholder="Buscar por código ou nome…"
            clearable={false}
          />
        </div>
      </FilterBar>

      <Tabs defaultValue="consolidado">
        <TabsList>
          <TabsTrigger value="consolidado">
            <FileBarChart className="mr-1 h-4 w-4" aria-hidden="true" /> Consolidado DRE
          </TabsTrigger>
          <TabsTrigger value="credenciados">
            <Receipt className="mr-1 h-4 w-4" aria-hidden="true" /> Credenciados
          </TabsTrigger>
        </TabsList>
        <TabsContent value="consolidado" className="space-y-3">
          {consolidado.isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{
                label: 'Tentar novamente',
                onClick: () => void consolidado.refetch(),
              }}
            />
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <p className="text-sm font-semibold">
                  Total: {formatarMoeda(totalConsolidado.toFixed(2))} •{' '}
                  {(consolidado.data ?? []).length} DRE(s)
                </p>
                <BotoesExportacao
                  tipo="consolidado"
                  semDados={(consolidado.data ?? []).length === 0}
                />
              </CardHeader>
              <CardContent className="p-0">
                <DataTable
                  columns={colunasConsolidado}
                  data={consolidado.data ?? []}
                  isLoading={consolidado.isLoading}
                  emptyTitle="Sem fechamentos"
                  emptyDescription="Não há dados consolidados para os filtros selecionados."
                  ariaLabel="Relatório consolidado por DRE"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="credenciados" className="space-y-3">
          {credenciados.isError ? (
            <ErrorState
              variant="load-failed"
              primaryAction={{
                label: 'Tentar novamente',
                onClick: () => void credenciados.refetch(),
              }}
            />
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <p className="text-sm font-semibold">
                  Total: {formatarMoeda(totalCredenciados.toFixed(2))} •{' '}
                  {(credenciados.data ?? []).length} demonstrativo(s)
                </p>
                <BotoesExportacao
                  tipo="credenciados"
                  semDados={(credenciados.data ?? []).length === 0}
                />
              </CardHeader>
              <CardContent className="p-0">
                <DataTable
                  columns={colunasCredenciado}
                  data={(credenciados.data ?? []).slice(0, 50)}
                  isLoading={credenciados.isLoading}
                  emptyTitle="Sem demonstrativos"
                  emptyDescription="Não há demonstrativos para os filtros selecionados."
                  ariaLabel="Relatório por credenciado"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <ResponsiveModal
        open={!!pdfPendente}
        onOpenChange={(aberto) => {
          if (!aberto) {
            definirPdfPendente(null);
            definirTextoConfirmacao('');
          }
        }}
        title={
          <>
            <Download className="h-5 w-5" aria-hidden="true" />
            Confirmar exportação PDF
            <DestructiveActionChip label="Ação irreversível" />
          </>
        }
        description="Esta exportação é protocolada no SEI e fica imutável no histórico. Use CSV ou XLSX para pré-visualizações."
      >
        <div className="space-y-3">
          {pendente && (
            <div className="space-y-1 rounded-lg bg-muted p-3 text-sm">
              <div>
                <span className="text-muted-foreground">Relatório:</span>{' '}
                <strong>{pendente.titulo}</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Competência:</span>{' '}
                <strong>{MESES.find((m) => m.value === competencia)?.label ?? competencia}</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Escopo:</span>{' '}
                <strong>
                  {dreAtual ? `DRE ${dreAtual.codigo ?? dreAtual.nome}` : 'Todas as DREs'}
                </strong>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="sof-relatorios-confirmar">
              Para confirmar, digite{' '}
              <code className="rounded bg-muted px-1 font-mono">EXPORTAR</code>
            </Label>
            <Input
              id="sof-relatorios-confirmar"
              value={textoConfirmacao}
              onChange={(e) => definirTextoConfirmacao(e.target.value.toUpperCase())}
              placeholder="EXPORTAR"
              autoComplete="off"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => definirPdfPendente(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmarPdf()}
              disabled={textoConfirmacao.trim() !== 'EXPORTAR' || registrar.isPending}
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              {registrar.isPending ? 'Exportando…' : 'Confirmar e exportar PDF'}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
}

export default SofRelatoriosPage;
